(function() {
  'use strict';

  /* ─── Helpers ─── */
  function debounce(fn, ms) {
    var timer;
    return function() {
      var ctx = this, args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function() { fn.apply(ctx, args); }, ms);
    };
  }

  function clamp(val, min, max) {
    return Math.max(min, Math.min(max, val));
  }

  /* ─── SVG namespace ─── */
  var SVG_NS = 'http://www.w3.org/2000/svg';

  /* ─── ViewBox constants (simple-world-map projection) ─── */
  var VB_X = -35.822;
  var VB_Y = 241.591;
  var VB_W = 917.254;
  var VB_H = 458.627;

  /* Approximate lat→Y conversion for coffee belt overlay.
     Linear fit from reference country bounding boxes:
     Y ≈ -2.9734 * lat + 531.58 */
  function latToY(lat) {
    return -2.9734 * lat + 531.58;
  }

  /* ================================================================
     CoffeeWorldMap
     ================================================================ */
  function CoffeeWorldMap(container, options) {
    this.container = container;
    this.options = options || {};
    this.data = window.WorldMapData || {};
    this.config = window.WorldMapConfig || {};

    /* Merge color overrides from data attributes */
    var colors = this.config.colors || {};
    if (this.options.highlightColor) { colors.activeStroke = this.options.highlightColor; }
    if (this.options.baseColor)      { colors.base = this.options.baseColor; }
    if (this.options.oceanColor)     { colors.ocean = this.options.oceanColor; }
    this.config.colors = colors;

    this.svg = null;
    this.mapGroup = null;
    this.tooltip = null;
    this.infoPanel = null;
    this.activeCountry = null;

    /* Pan / Zoom state */
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.minZoom = 1;
    this.maxZoom = 5;
    this.isPanning = false;
    this.panStartX = 0;
    this.panStartY = 0;
    this.panOriginX = 0;
    this.panOriginY = 0;

    /* Touch state */
    this.lastTouchDist = 0;

    this.init();
  }

  /* ─── Initialise ─── */
  CoffeeWorldMap.prototype.init = function() {
    this.container.innerHTML = '';
    this.container.classList.add('world-map__container');

    this.createSVG();
    this.renderOcean();
    this.renderCoffeeBelt();
    this.renderCountries();
    this.createTooltip();
    this.createInfoPanel();
    this.renderLegend();
    this.renderControls();
    this.bindEvents();
  };

  /* ─── Create SVG ─── */
  CoffeeWorldMap.prototype.createSVG = function() {
    this.svg = document.createElementNS(SVG_NS, 'svg');
    this.svg.setAttribute('viewBox', VB_X + ' ' + VB_Y + ' ' + VB_W + ' ' + VB_H);
    this.svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    this.svg.setAttribute('role', 'img');
    this.svg.setAttribute('aria-label', 'Weltkarte der Kaffee-Anbaugebiete');

    this.mapGroup = document.createElementNS(SVG_NS, 'g');
    this.mapGroup.setAttribute('class', 'world-map__map-group');
    this.svg.appendChild(this.mapGroup);
    this.container.appendChild(this.svg);
  };

  /* ─── Ocean background ─── */
  CoffeeWorldMap.prototype.renderOcean = function() {
    var rect = document.createElementNS(SVG_NS, 'rect');
    rect.setAttribute('x', VB_X);
    rect.setAttribute('y', VB_Y);
    rect.setAttribute('width', VB_W);
    rect.setAttribute('height', VB_H);
    rect.setAttribute('fill', this.config.colors.ocean || '#ffffff');
    this.mapGroup.appendChild(rect);
  };

  /* ─── Coffee belt band ─── */
  CoffeeWorldMap.prototype.renderCoffeeBelt = function() {
    var belt = this.config.coffeeBelt;
    if (!belt || !belt.show) return;

    var y1 = latToY(belt.north);
    var y2 = latToY(belt.south);
    var rect = document.createElementNS(SVG_NS, 'rect');
    rect.setAttribute('x', VB_X);
    rect.setAttribute('y', y1);
    rect.setAttribute('width', VB_W);
    rect.setAttribute('height', y2 - y1);
    rect.setAttribute('fill', this.config.colors.coffeeBelt || '#ffffff');
    rect.setAttribute('class', 'world-map__coffee-belt');
    this.mapGroup.appendChild(rect);
  };

  /* ─── Render all countries ─── */
  CoffeeWorldMap.prototype.renderCountries = function() {
    var countries = this.data;
    var regions = (this.config.regions) || {};
    var colors = this.config.colors;
    var keys = Object.keys(countries);

    for (var i = 0; i < keys.length; i++) {
      var code = keys[i];
      var country = countries[code];
      var region = regions[code];
      var paths = country.paths || [];

      for (var p = 0; p < paths.length; p++) {
        var pathEl = document.createElementNS(SVG_NS, 'path');
        pathEl.setAttribute('d', paths[p]);
        pathEl.setAttribute('class', 'world-map__country' + (region ? ' world-map__country--coffee' : ''));
        pathEl.setAttribute('data-code', code);
        pathEl.setAttribute('data-name', country.nameDE || country.name);

        if (region) {
          var tierColor = colors['tier' + region.tier] || colors.tier3;
          pathEl.setAttribute('fill', tierColor);
          pathEl.setAttribute('data-tier', region.tier);
        } else {
          pathEl.setAttribute('fill', colors.base || '#e0e0e0');
        }

        this.mapGroup.appendChild(pathEl);
      }
    }
  };


  /* ─── Tooltip element ─── */
  CoffeeWorldMap.prototype.createTooltip = function() {
    this.tooltip = this.container.querySelector('.world-map__tooltip');
    if (!this.tooltip) {
      this.tooltip = document.createElement('div');
      this.tooltip.className = 'world-map__tooltip';
      this.tooltip.setAttribute('aria-live', 'polite');
      this.container.appendChild(this.tooltip);
    }
  };

  /* ─── Info panel element ─── */
  CoffeeWorldMap.prototype.createInfoPanel = function() {
    /* Look for an existing panel after the container's parent wrapper */
    var wrapper = this.container.closest('.world-map-section') || this.container.parentElement;
    this.infoPanel = wrapper.querySelector('.world-map__info-panel');
    if (!this.infoPanel) {
      this.infoPanel = document.createElement('div');
      this.infoPanel.className = 'world-map__info-panel';
      this.container.parentNode.insertBefore(this.infoPanel, this.container.nextSibling);
    }
  };

  /* ─── Legend overlay ─── */
  CoffeeWorldMap.prototype.renderLegend = function() {
    var legend = this.config.legend;
    if (!legend) return;

    var el = document.createElement('div');
    el.className = 'world-map__legend';

    var title = document.createElement('div');
    title.className = 'world-map__legend-title';
    title.textContent = legend.title;
    el.appendChild(title);

    var items = legend.items || [];
    for (var i = 0; i < items.length; i++) {
      var item = document.createElement('div');
      item.className = 'world-map__legend-item';

      var swatch = document.createElement('span');
      swatch.className = 'world-map__legend-swatch' + (items[i].marker ? ' world-map__legend-swatch--marker' : '');
      if (items[i].color) swatch.style.backgroundColor = items[i].color;

      var label = document.createElement('span');
      label.className = 'world-map__legend-label';
      label.textContent = items[i].label;

      item.appendChild(swatch);
      item.appendChild(label);
      el.appendChild(item);
    }

    this.container.appendChild(el);
  };

  /* ─── Zoom controls ─── */
  CoffeeWorldMap.prototype.renderControls = function() {
    var self = this;
    var wrap = document.createElement('div');
    wrap.className = 'world-map__controls';

    var btnPlus = document.createElement('button');
    btnPlus.className = 'world-map__control-btn';
    btnPlus.setAttribute('type', 'button');
    btnPlus.setAttribute('aria-label', 'Hineinzoomen');
    btnPlus.textContent = '+';
    btnPlus.addEventListener('click', function() { self.zoomBy(0.5); });

    var btnMinus = document.createElement('button');
    btnMinus.className = 'world-map__control-btn';
    btnMinus.setAttribute('type', 'button');
    btnMinus.setAttribute('aria-label', 'Herauszoomen');
    btnMinus.textContent = '\u2212';
    btnMinus.addEventListener('click', function() { self.zoomBy(-0.5); });

    var btnReset = document.createElement('button');
    btnReset.className = 'world-map__control-btn world-map__control-btn--reset';
    btnReset.setAttribute('type', 'button');
    btnReset.setAttribute('aria-label', 'Ansicht zur\u00fccksetzen');
    btnReset.textContent = '\u21ba';
    btnReset.addEventListener('click', function() { self.resetView(); });

    wrap.appendChild(btnPlus);
    wrap.appendChild(btnMinus);
    wrap.appendChild(btnReset);
    this.container.appendChild(wrap);
  };

  /* ─── Event binding (delegation) ─── */
  CoffeeWorldMap.prototype.bindEvents = function() {
    var self = this;

    /* Hover — delegated on SVG */
    this.svg.addEventListener('mousemove', function(e) {
      var target = e.target;
      if (target.classList.contains('world-map__country--coffee')) {
        self.showTooltip(target, e);
      } else {
        self.hideTooltip();
      }
    });

    this.svg.addEventListener('mouseleave', function() {
      self.hideTooltip();
    });

    /* Click — delegated on SVG */
    this.svg.addEventListener('click', function(e) {
      var target = e.target;
      if (target.classList.contains('world-map__country--coffee')) {
        var code = target.getAttribute('data-code');
        self.selectCountry(code);
      }
    });

    /* ─── Pan (mouse) ─── */
    this.container.addEventListener('mousedown', function(e) {
      if (e.target.closest('.world-map__controls') || e.target.closest('.world-map__legend')) return;
      self.isPanning = true;
      self.panStartX = e.clientX;
      self.panStartY = e.clientY;
      self.panOriginX = self.panX;
      self.panOriginY = self.panY;
      e.preventDefault();
    });

    document.addEventListener('mousemove', function(e) {
      if (!self.isPanning) return;
      var dx = e.clientX - self.panStartX;
      var dy = e.clientY - self.panStartY;
      self.panTo(self.panOriginX + dx, self.panOriginY + dy);
    });

    document.addEventListener('mouseup', function() {
      self.isPanning = false;
    });

    /* ─── Zoom (wheel) ─── */
    this.container.addEventListener('wheel', function(e) {
      e.preventDefault();
      var delta = e.deltaY > 0 ? -0.25 : 0.25;
      self.zoomBy(delta);
    }, { passive: false });

    /* ─── Touch: drag + pinch ─── */
    this.container.addEventListener('touchstart', function(e) {
      if (e.target.closest('.world-map__controls') || e.target.closest('.world-map__legend')) return;
      if (e.touches.length === 1) {
        self.isPanning = true;
        self.panStartX = e.touches[0].clientX;
        self.panStartY = e.touches[0].clientY;
        self.panOriginX = self.panX;
        self.panOriginY = self.panY;
      } else if (e.touches.length === 2) {
        self.isPanning = false;
        self.lastTouchDist = self.getTouchDist(e.touches);
      }
    }, { passive: true });

    this.container.addEventListener('touchmove', function(e) {
      e.preventDefault();
      if (e.touches.length === 1 && self.isPanning) {
        var dx = e.touches[0].clientX - self.panStartX;
        var dy = e.touches[0].clientY - self.panStartY;
        self.panTo(self.panOriginX + dx, self.panOriginY + dy);
      } else if (e.touches.length === 2) {
        var dist = self.getTouchDist(e.touches);
        if (self.lastTouchDist > 0) {
          var scale = dist / self.lastTouchDist;
          var newZoom = clamp(self.zoom * scale, self.minZoom, self.maxZoom);
          self.setZoom(newZoom);
        }
        self.lastTouchDist = dist;
      }
    }, { passive: false });

    this.container.addEventListener('touchend', function() {
      self.isPanning = false;
      self.lastTouchDist = 0;
    }, { passive: true });

    /* ─── Resize debounce ─── */
    window.addEventListener('resize', debounce(function() {
      self.constrainPan();
      self.applyTransform();
    }, 200));
  };

  /* ─── Touch distance helper ─── */
  CoffeeWorldMap.prototype.getTouchDist = function(touches) {
    var dx = touches[0].clientX - touches[1].clientX;
    var dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  /* ─── Tooltip show / hide ─── */
  CoffeeWorldMap.prototype.showTooltip = function(pathEl, e) {
    var code = pathEl.getAttribute('data-code');
    var region = (this.config.regions || {})[code];
    if (!region) return;

    var html = '<div class="world-map__tooltip-title">' + region.title + '</div>';
    if (region.subRegions && region.subRegions.length) {
      html += '<div class="world-map__tooltip-regions">' + region.subRegions.map(function(sr) { return sr.name; }).join(', ') + '</div>';
    }
    this.tooltip.innerHTML = html;
    this.tooltip.classList.add('world-map__tooltip--visible');

    /* Position near cursor but stay within container */
    var rect = this.container.getBoundingClientRect();
    var x = e.clientX - rect.left;
    var y = e.clientY - rect.top;
    var tw = this.tooltip.offsetWidth;
    var th = this.tooltip.offsetHeight;

    var left = x - tw / 2;
    var top = y - th - 14;

    if (left < 4) left = 4;
    if (left + tw > rect.width - 4) left = rect.width - tw - 4;
    if (top < 4) {
      top = y + 18;
      /* flip arrow */
      this.tooltip.style.setProperty('--arrow', 'top');
    }

    this.tooltip.style.left = left + 'px';
    this.tooltip.style.top = top + 'px';
  };

  CoffeeWorldMap.prototype.hideTooltip = function() {
    if (this.tooltip) {
      this.tooltip.classList.remove('world-map__tooltip--visible');
    }
  };

  /* ─── Highlight multiple countries (product origins) ─── */
  CoffeeWorldMap.prototype.highlightCountries = function(codes) {
    /* Clear previous highlights */
    var prev = this.container.querySelectorAll('.world-map__country--active');
    for (var i = 0; i < prev.length; i++) {
      prev[i].classList.remove('world-map__country--active');
    }

    /* Close info panel — multi-country doesn't map to one panel */
    this.closeInfoPanel();
    this.activeCountry = null;

    /* Add active class to all paths matching any code */
    for (var c = 0; c < codes.length; c++) {
      var paths = this.container.querySelectorAll('[data-code="' + codes[c] + '"]');
      for (var j = 0; j < paths.length; j++) {
        paths[j].classList.add('world-map__country--active');
      }
    }
  };

  /* ─── Origin markers: one pulsing dot per country we buy from ─── */
  CoffeeWorldMap.prototype.markOrigins = function(codes) {
    var regions = this.config.regions || {};
    var old = this.mapGroup.querySelectorAll('.world-map__origin');
    for (var i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);

    for (var c = 0; c < codes.length; c++) {
      var r = regions[codes[c]];
      if (!r || r.markerX == null) continue;
      var g = document.createElementNS(SVG_NS, 'g');
      g.setAttribute('class', 'world-map__origin');
      var ring = document.createElementNS(SVG_NS, 'circle');
      ring.setAttribute('class', 'world-map__origin-ring');
      ring.setAttribute('cx', r.markerX);
      ring.setAttribute('cy', r.markerY);
      ring.setAttribute('r', 6);
      ring.style.animationDelay = (c * 0.4) + 's';
      var dot = document.createElementNS(SVG_NS, 'circle');
      dot.setAttribute('class', 'world-map__origin-dot');
      dot.setAttribute('cx', r.markerX);
      dot.setAttribute('cy', r.markerY);
      dot.setAttribute('r', 2.4);
      g.appendChild(ring);
      g.appendChild(dot);
      this.mapGroup.appendChild(g);
    }
  };

  /* ─── Zoom/pan so that the given countries' markers fill the view ─── */
  CoffeeWorldMap.prototype.fitTo = function(codes) {
    var regions = this.config.regions || {};
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (var i = 0; i < codes.length; i++) {
      var r = regions[codes[i]];
      if (!r || r.markerX == null) continue;
      minX = Math.min(minX, r.markerX); maxX = Math.max(maxX, r.markerX);
      minY = Math.min(minY, r.markerY); maxY = Math.max(maxY, r.markerY);
    }
    if (minX === Infinity) return;

    /* viewBox → px inside the svg element (preserveAspectRatio meet) */
    var W = this.container.clientWidth, H = this.container.clientHeight;
    var sc = Math.min(W / VB_W, H / VB_H);
    var ox = (W - VB_W * sc) / 2, oy = (H - VB_H * sc) / 2;
    var bw = (maxX - minX) * sc, bh = (maxY - minY) * sc;
    var cx = ox + ((minX + maxX) / 2 - VB_X) * sc;
    var cy = oy + ((minY + maxY) / 2 - VB_Y) * sc;

    this.zoom = clamp(Math.min(W / (bw + 80), H / (bh + 80)), this.minZoom, this.maxZoom);
    this.panX = -(cx - W / 2) * this.zoom;
    this.panY = -(cy - H / 2) * this.zoom;
    this.constrainPan();
    this.applyTransform();
  };

  /* ─── Select / deselect country ─── */
  CoffeeWorldMap.prototype.selectCountry = function(code) {
    /* Deselect previous */
    var prev = this.container.querySelectorAll('.world-map__country--active');
    for (var i = 0; i < prev.length; i++) {
      prev[i].classList.remove('world-map__country--active');
    }

    if (this.activeCountry === code) {
      this.activeCountry = null;
      this.closeInfoPanel();
      return;
    }

    this.activeCountry = code;

    /* Highlight all path segments for this country */
    var paths = this.container.querySelectorAll('[data-code="' + code + '"]');
    for (var j = 0; j < paths.length; j++) {
      paths[j].classList.add('world-map__country--active');
    }

    this.openInfoPanel(code);
  };

  /* ─── Info panel open ─── */
  CoffeeWorldMap.prototype.openInfoPanel = function(code) {
    var region = (this.config.regions || {})[code];
    if (!region || !this.infoPanel) return;

    var stats = region.stats || {};
    var statLabels = {
      rank: 'Weltrang',
      production: 'Produktion',
      altitude: 'Anbauh\u00f6he',
      varieties: 'Variet\u00e4ten',
      process: 'Aufbereitung',
      harvest: 'Ernte'
    };

    var html = '<div class="world-map__info-header">';
    html += '<h3 class="world-map__info-title">' + region.title + '</h3>';
    html += '<button class="world-map__info-close" aria-label="Schlie\u00dfen" type="button">&times;</button>';
    html += '</div>';

    if (region.description) {
      html += '<p class="world-map__info-description">' + region.description + '</p>';
    }

    html += '<div class="world-map__info-stats">';
    var statKeys = Object.keys(statLabels);
    for (var i = 0; i < statKeys.length; i++) {
      var key = statKeys[i];
      if (stats[key] == null) continue;
      html += '<div class="world-map__info-stat">';
      html += '<div class="world-map__info-stat-label">' + statLabels[key] + '</div>';
      html += '<div class="world-map__info-stat-value">' + stats[key] + '</div>';
      html += '</div>';
    }
    html += '</div>';

    if (region.subRegions && region.subRegions.length) {
      html += '<div class="world-map__info-subregions">';
      html += '<div class="world-map__info-subregions-title">Anbauregionen</div>';
      html += '<div class="world-map__info-subregion-tags">';
      for (var s = 0; s < region.subRegions.length; s++) {
        html += '<button type="button" class="world-map__info-subregion-tag" data-index="' + s + '">' + region.subRegions[s].name + '</button>';
      }
      html += '</div>';
      html += '<div class="world-map__info-subregion-detail"></div>';
      html += '</div>';
    }

    this.infoPanel.innerHTML = html;
    this.infoPanel.classList.add('world-map__info-panel--open');

    /* Close button handler */
    var self = this;
    var closeBtn = this.infoPanel.querySelector('.world-map__info-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function() {
        self.selectCountry(self.activeCountry);
      });
    }

    /* Sub-region tag click handlers */
    var tagButtons = this.infoPanel.querySelectorAll('.world-map__info-subregion-tag');
    var detailDiv = this.infoPanel.querySelector('.world-map__info-subregion-detail');
    var activeIndex = -1;

    for (var t = 0; t < tagButtons.length; t++) {
      (function(btn) {
        btn.addEventListener('click', function() {
          var idx = parseInt(btn.getAttribute('data-index'), 10);
          var sr = region.subRegions[idx];

          /* Toggle: clicking same tag again collapses */
          if (activeIndex === idx) {
            activeIndex = -1;
            btn.classList.remove('world-map__info-subregion-tag--active');
            detailDiv.innerHTML = '';
            detailDiv.classList.remove('world-map__info-subregion-detail--open');
            return;
          }

          /* Remove active from all tags */
          for (var a = 0; a < tagButtons.length; a++) {
            tagButtons[a].classList.remove('world-map__info-subregion-tag--active');
          }
          btn.classList.add('world-map__info-subregion-tag--active');
          activeIndex = idx;

          /* Build detail HTML */
          var dHtml = '';
          if (sr.description) {
            dHtml += '<p class="world-map__info-subregion-detail-desc">' + sr.description + '</p>';
          }
          dHtml += '<div class="world-map__info-subregion-detail-stats">';
          if (sr.altitude)  dHtml += '<div class="world-map__info-subregion-detail-stat"><span class="world-map__info-subregion-detail-stat-label">Anbauhöhe</span><span class="world-map__info-subregion-detail-stat-value">' + sr.altitude + '</span></div>';
          if (sr.varieties) dHtml += '<div class="world-map__info-subregion-detail-stat"><span class="world-map__info-subregion-detail-stat-label">Varietäten</span><span class="world-map__info-subregion-detail-stat-value">' + sr.varieties + '</span></div>';
          if (sr.process)   dHtml += '<div class="world-map__info-subregion-detail-stat"><span class="world-map__info-subregion-detail-stat-label">Aufbereitung</span><span class="world-map__info-subregion-detail-stat-value">' + sr.process + '</span></div>';
          if (sr.harvest)   dHtml += '<div class="world-map__info-subregion-detail-stat"><span class="world-map__info-subregion-detail-stat-label">Ernte</span><span class="world-map__info-subregion-detail-stat-value">' + sr.harvest + '</span></div>';
          dHtml += '</div>';

          detailDiv.innerHTML = dHtml;
          detailDiv.classList.add('world-map__info-subregion-detail--open');
        });
      })(tagButtons[t]);
    }

    /* Scroll into view on mobile */
    if (window.innerWidth <= 768) {
      this.infoPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  CoffeeWorldMap.prototype.closeInfoPanel = function() {
    if (this.infoPanel) {
      this.infoPanel.classList.remove('world-map__info-panel--open');
    }
  };

  /* ─── Zoom helpers ─── */
  CoffeeWorldMap.prototype.zoomBy = function(delta) {
    this.setZoom(this.zoom + delta);
  };

  CoffeeWorldMap.prototype.setZoom = function(newZoom) {
    this.zoom = clamp(newZoom, this.minZoom, this.maxZoom);
    this.constrainPan();
    this.applyTransform();
  };

  CoffeeWorldMap.prototype.resetView = function() {
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.applyTransform();
  };

  /* ─── Pan helpers ─── */
  CoffeeWorldMap.prototype.panTo = function(x, y) {
    this.panX = x;
    this.panY = y;
    this.constrainPan();
    this.applyTransform();
  };

  CoffeeWorldMap.prototype.constrainPan = function() {
    var rect = this.container.getBoundingClientRect();
    var w = rect.width;
    var h = rect.height;
    var maxPanX = (w * (this.zoom - 1)) / 2;
    var maxPanY = (h * (this.zoom - 1)) / 2;
    this.panX = clamp(this.panX, -maxPanX, maxPanX);
    this.panY = clamp(this.panY, -maxPanY, maxPanY);
  };

  CoffeeWorldMap.prototype.applyTransform = function() {
    var t = 'translate(' + this.panX + 'px, ' + this.panY + 'px) scale(' + this.zoom + ')';
    this.svg.style.transform = t;
    this.svg.style.transformOrigin = 'center center';
  };

  /* ================================================================
     Bootstrap
     ================================================================ */

  /* ─── Shared data helpers ─── */
  var SKIP_SKUS = { 'no-05': true, 'no-07': true };

  function getNameToCodeMap() {
    var regions = (window.WorldMapConfig && window.WorldMapConfig.regions) || {};
    var nameToCode = {};
    var codes = Object.keys(regions);
    for (var i = 0; i < codes.length; i++) {
      nameToCode[regions[codes[i]].title] = codes[i];
    }
    return nameToCode;
  }

  /* Shared fetch promise — cached so both bar + details reuse it */
  var _beansPromise = null;
  function fetchBeans() {
    if (_beansPromise) return _beansPromise;
    var bar = document.getElementById('product-bar');
    var url = bar && bar.getAttribute('data-beans-url');
    if (!url) return Promise.resolve(null);
    _beansPromise = fetch(url).then(function(r) { return r.json(); });
    return _beansPromise;
  }

  /* ================================================================
     Product Bar — renders product cards from beans.json
     ================================================================ */
  function buildProductBar(mapInstance) {
    var bar = document.getElementById('product-bar');
    if (!bar) return;

    var nameToCode = getNameToCodeMap();

    fetchBeans().then(function(data) {
        if (!data) return;
        var products = data.products || [];
        var activeProduct = null;

        for (var p = 0; p < products.length; p++) {
          (function(product) {
            if (SKIP_SKUS[product.sku]) return;

            /* Resolve origin codes */
            var originCodes = [];
            var origins = product.origins || [];
            for (var o = 0; o < origins.length; o++) {
              var code = nameToCode[origins[o].country];
              if (code) originCodes.push(code);
            }

            var card = document.createElement('button');
            card.className = 'world-map__product-card';
            card.setAttribute('type', 'button');
            card.setAttribute('data-origins', originCodes.join(','));
            card.setAttribute('data-sku', product.sku);

            /* Short display name: "No. 03" from the full name */
            var shortName = product.name.split(' - ')[1] || product.name;

            if (product.image_url) {
              var img = document.createElement('img');
              img.className = 'world-map__product-img';
              img.src = product.image_url;
              img.alt = product.name;
              img.loading = 'lazy';
              card.appendChild(img);
            } else {
              var placeholder = document.createElement('div');
              placeholder.className = 'world-map__product-img-placeholder';
              placeholder.textContent = shortName;
              card.appendChild(placeholder);
            }

            var label = document.createElement('span');
            label.className = 'world-map__product-label';
            label.textContent = product.name.split(' - ')[0];
            var sub = document.createElement('small');
            sub.textContent = shortName;
            label.appendChild(sub);
            card.appendChild(label);

            /* Click handler — toggle */
            card.addEventListener('click', function() {
              var allCards = bar.querySelectorAll('.world-map__product-card');
              if (activeProduct === product.sku) {
                /* Deselect */
                activeProduct = null;
                for (var a = 0; a < allCards.length; a++) {
                  allCards[a].classList.remove('world-map__product-card--active');
                }
                /* Clear map highlights */
                mapInstance.highlightCountries([]);
              } else {
                /* Select this product */
                activeProduct = product.sku;
                for (var a = 0; a < allCards.length; a++) {
                  allCards[a].classList.remove('world-map__product-card--active');
                }
                card.classList.add('world-map__product-card--active');
                mapInstance.highlightCountries(originCodes);
              }
            });

            bar.appendChild(card);
          })(products[p]);
        }
      });
  }

  /* ================================================================
     Origin chapters — one per country we buy from, built from
     beans.json (which product, which region) + WorldMapConfig (terroir)
     ================================================================ */
  var CONTINENT = {
    BR: 'S\u00fcdamerika', CO: 'S\u00fcdamerika', PE: 'S\u00fcdamerika',
    GT: 'Mittelamerika', CR: 'Mittelamerika', HN: 'Mittelamerika', MX: 'Mittelamerika',
    IN: 'Asien', VN: 'Asien', ID: 'Asien', PG: 'Ozeanien',
    ET: 'Afrika', KE: 'Afrika', UG: 'Afrika', TZ: 'Afrika', RW: 'Afrika'
  };

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function fmtNum(n) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.'); }

  /* { code, regions:[names], beans:[types], products:[...] } per country, most-used first */
  function collectOrigins(products) {
    var nameToCode = getNameToCodeMap();
    var regions = (window.WorldMapConfig && window.WorldMapConfig.regions) || {};
    var byCode = {};
    for (var p = 0; p < products.length; p++) {
      var product = products[p];
      if (SKIP_SKUS[product.sku]) continue;
      var origins = product.origins || [];
      for (var o = 0; o < origins.length; o++) {
        var code = nameToCode[origins[o].country];
        if (!code) continue;
        var e = byCode[code] || (byCode[code] = { code: code, regions: [], beans: [], products: [] });
        if (origins[o].region && e.regions.indexOf(origins[o].region) < 0) e.regions.push(origins[o].region);
        if (origins[o].bean_type && e.beans.indexOf(origins[o].bean_type) < 0) e.beans.push(origins[o].bean_type);
        if (e.products.indexOf(product) < 0) e.products.push(product);
      }
    }
    return Object.keys(byCode).map(function(k) { return byCode[k]; }).sort(function(a, b) {
      return (b.products.length - a.products.length) || regions[a.code].title.localeCompare(regions[b.code].title, 'de');
    });
  }

  /* First config sub-region whose name matches one of the product regions */
  function matchSubRegion(origin) {
    var region = window.WorldMapConfig.regions[origin.code];
    var subs = (region && region.subRegions) || [];
    for (var i = 0; i < subs.length; i++) {
      if (origin.regions.indexOf(subs[i].name) >= 0) return subs[i];
    }
    return null;
  }

  function buildHeroStats(origins) {
    var box = document.getElementById('wm-stats');
    if (!box) return;
    var regions = window.WorldMapConfig.regions;
    var lo = Infinity, hi = 0, skus = {};
    for (var i = 0; i < origins.length; i++) {
      var sr = matchSubRegion(origins[i]);
      var alt = (sr && sr.altitude) || ((regions[origins[i].code].stats || {}).altitude) || '';
      var nums = alt.replace(/\./g, '').match(/\d+/g) || [];
      for (var n = 0; n < nums.length; n++) { lo = Math.min(lo, +nums[n]); hi = Math.max(hi, +nums[n]); }
      for (var p = 0; p < origins[i].products.length; p++) skus[origins[i].products[p].sku] = 1;
    }
    var stats = [['Herkunftsl\u00e4nder', origins.length], ['Kaffees', Object.keys(skus).length]];
    if (hi) stats.push(['Anbauh\u00f6he', fmtNum(lo) + '\u2013' + fmtNum(hi) + ' m']);

    box.innerHTML = '';
    for (var s = 0; s < stats.length; s++) {
      var d = el('div');
      d.appendChild(el('dt', null, stats[s][0]));
      d.appendChild(el('dd', null, stats[s][1]));
      box.appendChild(d);
    }
  }

  function productChip(product) {
    var parts = product.name.split(' - ');
    var a = el('a', 'wm__chip');
    a.href = product.url;
    if (product.image_url) {
      var img = el('img');
      img.src = product.image_url;
      img.alt = '';
      img.loading = 'lazy';
      a.appendChild(img);
    }
    var label = el('span', null, parts[0]);
    if (parts[1]) label.appendChild(el('small', null, parts[1]));
    a.appendChild(label);
    if (product.availability && product.availability !== 'in_stock') {
      a.appendChild(el('span', 'wm__chip-soldout', 'ausverkauft'));
    }
    return a;
  }

  function buildOriginChapters(origins, mapInstance) {
    var container = document.getElementById('product-details');
    if (!container) return;
    var regions = window.WorldMapConfig.regions;
    var images = {};
    try { images = JSON.parse(document.getElementById('world-map-images').textContent); } catch (e) { /* no photos, chapters still render */ }

    container.innerHTML = '';
    for (var i = 0; i < origins.length; i++) {
      (function(o, idx) {
        var region = regions[o.code];
        var sr = matchSubRegion(o);
        var stats = sr || region.stats || {};

        var art = el('article', 'wm__chapter');
        art.id = 'herkunft-' + o.code.toLowerCase();

        var media = el('div', 'wm__chapter-media');
        media.setAttribute('data-no', pad2(idx + 1));
        if (images[o.code]) {
          var img = el('img');
          img.src = images[o.code];
          img.alt = 'Kaffeeanbau in ' + (sr ? sr.name + ', ' : '') + region.title;
          img.loading = 'lazy';
          img.width = 1600;
          img.height = 1195;
          media.appendChild(img);
        }
        art.appendChild(media);

        var body = el('div', 'wm__chapter-body');
        var meta = [CONTINENT[o.code], o.beans.join(' & ')].filter(Boolean).join(' \u00b7 ');
        body.appendChild(el('p', 'wm__chapter-meta', meta));
        body.appendChild(el('h3', 'wm__chapter-title', region.title));
        if (o.regions.length) body.appendChild(el('p', 'wm__chapter-region', o.regions.join(' \u00b7 ')));
        var text = (sr && sr.description) || region.description;
        if (text) body.appendChild(el('p', 'wm__chapter-text', text));

        var rows = [['Anbauh\u00f6he', stats.altitude], ['Variet\u00e4ten', stats.varieties], ['Aufbereitung', stats.process], ['Ernte', stats.harvest]];
        var dl = el('dl', 'wm__chapter-stats');
        for (var r = 0; r < rows.length; r++) {
          if (!rows[r][1]) continue;
          var d = el('div');
          d.appendChild(el('dt', null, rows[r][0]));
          d.appendChild(el('dd', null, rows[r][1]));
          dl.appendChild(d);
        }
        if (dl.children.length) body.appendChild(dl);

        body.appendChild(el('p', 'wm__chapter-products-title', o.products.length > 1 ? 'Steckt in diesen Kaffees' : 'Steckt in diesem Kaffee'));
        var chips = el('div', 'wm__chapter-products');
        for (var p = 0; p < o.products.length; p++) chips.appendChild(productChip(o.products[p]));
        body.appendChild(chips);

        var link = el('button', 'wm__chapter-maplink', 'Auf der Karte zeigen');
        link.type = 'button';
        link.addEventListener('click', function() {
          if (mapInstance.activeCountry !== o.code) mapInstance.selectCountry(o.code);
          mapInstance.container.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        body.appendChild(link);

        art.appendChild(body);
        container.appendChild(art);
      })(origins[i], i);
    }
  }

  function boot() {
    var mapEl = document.getElementById('coffee-map');
    if (!mapEl) return;

    /* Reset shared fetch cache on re-boot (theme editor) */
    _beansPromise = null;

    var map = new CoffeeWorldMap(mapEl, {
      highlightColor: mapEl.getAttribute('data-highlight-color') || null,
      baseColor:      mapEl.getAttribute('data-base-color') || null,
      oceanColor:     mapEl.getAttribute('data-ocean-color') || null
    });

    /* Expose globally so product bar can call it */
    window.coffeeWorldMap = map;

    buildProductBar(map);

    fetchBeans().then(function(data) {
      if (!data) return;
      var origins = collectOrigins(data.products || []);
      var codes = origins.map(function(o) { return o.code; });
      map.markOrigins(codes);
      /* Small screens: the whole world is too small to read, frame our origins instead */
      if (mapEl.clientWidth < 600) map.fitTo(codes);
      buildHeroStats(origins);
      buildOriginChapters(origins, map);
    }).catch(function(err) {
      console.error('world-map: beans.json konnte nicht geladen werden', err);
    });
  }

  /* Run on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify theme editor re-render */
  document.addEventListener('shopify:section:load', function(e) {
    if (e.target.querySelector('#coffee-map')) {
      boot();
    }
  });

})();
